import { useEffect, useState } from "react";
import api, { fmtTRY, fmtDate } from "../lib/api";
import { toast } from "sonner";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "../components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "../components/ui/tabs";
import MediaUploader from "../components/common/MediaUploader";
import CustomerPicker from "../components/common/CustomerPicker";
import { Plus, Pencil, Trash2, CheckCircle2, X, PackagePlus, Search, FileCode } from "lucide-react";
import { downloadFile } from "../lib/download";

const STATUS = {
  kabul: { label: "Kabul Edildi", color: "bg-slate-500 text-white" },
  parca_bekliyor: { label: "Parça Bekliyor", color: "bg-amber-500 text-slate-900" },
  onarimda: { label: "Onarımda", color: "bg-[#EA580C] text-white" },
  test: { label: "Test Edildi", color: "bg-blue-500 text-white" },
  teslim: { label: "Teslim Edildi", color: "bg-emerald-600 text-white" },
};

const empty = {
  customer_id: "", customer_name: "", plate: "", chassis_no: "",
  tractor_brand: "", tractor_model: "", working_hours: "",
  complaint: "", diagnosis: "", technician: "",
  status: "kabul", parts: [],
  labor_hours: 0, labor_rate: 0, labor_cost: 0,
  notes: "", media: [],
};

// Searchable part picker inline component
function PartSearchPicker({ parts, onPick }) {
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const filtered = q ? parts.filter((p) =>
    (p.name + " " + (p.oem_no || "") + " " + (p.category || "")).toLowerCase().includes(q.toLowerCase())
  ).slice(0, 12) : parts.slice(0, 12);
  return (
    <div className="relative">
      <div className="flex items-center gap-2 border border-slate-300 rounded-lg px-3 h-10 bg-white">
        <Search size={16} className="text-slate-400" />
        <input
          className="flex-1 bg-transparent outline-none text-sm placeholder:text-slate-400"
          placeholder="Parça ismi veya OEM/parça no ile ara…"
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          onBlur={() => setTimeout(() => setOpen(false), 200)}
          data-testid="wo-part-search"
        />
      </div>
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-slate-200 rounded-lg shadow-lg max-h-64 overflow-y-auto">
          {filtered.length === 0 && <div className="p-3 text-sm text-slate-400">Sonuç yok</div>}
          {filtered.map((p) => (
            <button
              key={p.id}
              type="button"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => { onPick(p); setQ(""); setOpen(false); }}
              className="w-full text-left px-3 py-2 hover:bg-slate-50 border-b border-slate-100 last:border-0"
              data-testid={`wo-part-option-${p.id}`}
            >
              <div className="flex items-center justify-between gap-2">
                <div className="min-w-0">
                  <div className="font-semibold text-slate-900 text-sm truncate">{p.name}</div>
                  <div className="text-[11px] text-slate-500 font-mono">{p.oem_no || "—"} · {p.category || ""}</div>
                </div>
                <div className="text-right shrink-0">
                  <div className="text-xs text-slate-500">Stok: <span className={p.quantity <= p.min_quantity ? "text-rose-600 font-bold" : "font-bold"}>{p.quantity}</span></div>
                  <div className="text-sm font-bold text-[#1B4D3E]">{new Intl.NumberFormat("tr-TR").format(p.sale_price)} ₺</div>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

export default function WorkOrdersPage() {
  const [items, setItems] = useState([]);
  const [tab, setTab] = useState("all");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [parts, setParts] = useState([]);
  const [prFor, setPrFor] = useState(null);
  const [prForm, setPrForm] = useState({ part_id: "", part_name: "", quantity: 1, note: "" });

  const load = () => api.get("/work-orders").then((r) => setItems(r.data));
  useEffect(() => {
    load();
    api.get("/customers").then((r) => setCustomers(r.data));
    api.get("/parts").then((r) => setParts(r.data));
  }, []);

  const filtered = items.filter((w) => tab === "all" ? true : w.status === tab);

  const addPartLine = (partIdOrObj) => {
    const p = typeof partIdOrObj === "object" ? partIdOrObj : parts.find((x) => x.id === partIdOrObj);
    if (!p) return;
    setForm((f) => ({ ...f, parts: [...f.parts, { part_id: p.id, name: p.name, quantity: 1, unit_price: p.sale_price }] }));
  };
  const updatePartLine = (i, patch) => {
    const list = [...form.parts]; list[i] = { ...list[i], ...patch };
    setForm({ ...form, parts: list });
  };
  const removePartLine = (i) => setForm({ ...form, parts: form.parts.filter((_, idx) => idx !== i) });

  const laborCost = Number(form.labor_hours || 0) * Number(form.labor_rate || 0) || Number(form.labor_cost || 0);
  const total = form.parts.reduce((s, p) => s + Number(p.quantity) * Number(p.unit_price), 0) + laborCost;

  const submit = async () => {
    if (!form.customer_name.trim()) return toast.error("Müşteri adı zorunlu");
    const computed_labor_cost = Number(form.labor_hours || 0) * Number(form.labor_rate || 0);
    const payload = {
      ...form,
      working_hours: form.working_hours ? Number(form.working_hours) : null,
      labor_hours: Number(form.labor_hours) || 0,
      labor_rate: Number(form.labor_rate) || 0,
      labor_cost: computed_labor_cost || Number(form.labor_cost) || 0,
      parts: form.parts.map((p) => ({ ...p, quantity: Number(p.quantity), unit_price: Number(p.unit_price) })),
    };
    try {
      if (editing) await api.put(`/work-orders/${editing}`, payload);
      else await api.post("/work-orders", payload);
      toast.success("Kaydedildi"); setOpen(false); setForm(empty); setEditing(null); load();
    } catch { toast.error("Kayıt başarısız"); }
  };

  const edit = (w) => { setEditing(w.id); setForm({ ...empty, ...w, parts: w.parts || [], media: w.media || [] }); setOpen(true); };
  const remove = async (id) => { if (window.confirm("Silinsin mi?")) { await api.delete(`/work-orders/${id}`); toast.success("Silindi"); load(); } };
  const deliver = async (id) => { await api.post(`/work-orders/${id}/deliver`); toast.success("Teslim edildi, stok düşüldü, gelir kaydı yapıldı"); load(); };

  const openPr = (w) => {
    setPrFor(w);
    setPrForm({ part_id: "", part_name: "", quantity: 1, note: "" });
  };
  const submitPr = async () => {
    if (!prForm.part_name) return toast.error("Parça adı gerekli");
    try {
      await api.post("/part-requests", {
        ...prForm,
        quantity: Number(prForm.quantity) || 1,
        work_order_id: prFor.id,
        work_order_no: prFor.wo_no,
      });
      toast.success("Talep gönderildi. Depoya anlık bildirim düştü.");
      setPrFor(null);
    } catch { toast.error("Talep gönderilemedi"); }
  };

  return (
    <div className="space-y-5" data-testid="workorders-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#1B4D3E]">Servis</div>
          <h1 className="text-2xl sm:text-3xl lg:text-4xl font-extrabold tracking-tight text-slate-900">İş Emirleri</h1>
        </div>
        <Button className="bg-[#D97706] hover:bg-[#B45309] text-white gap-2" onClick={() => { setEditing(null); setForm(empty); setOpen(true); }} data-testid="add-wo-btn">
          <Plus size={16} /> Yeni İş Emri
        </Button>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="flex-wrap">
          <TabsTrigger value="all" data-testid="wo-tab-all">Tümü</TabsTrigger>
          {Object.entries(STATUS).map(([k, v]) => <TabsTrigger key={k} value={k} data-testid={`wo-tab-${k}`}>{v.label}</TabsTrigger>)}
        </TabsList>
      </Tabs>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 sm:gap-4">
        {filtered.map((w) => (
          <Card key={w.id} className="border-slate-200 tp-fade" data-testid={`wo-card-${w.id}`}>
            <CardContent className="p-4 space-y-2">
              <div className="flex items-start justify-between gap-2">
                <div>
                  <div className="text-xs text-slate-500 font-mono">{w.wo_no}</div>
                  <div className="font-bold text-slate-900">{w.customer_name}</div>
                  <div className="text-xs text-slate-500">{w.tractor_brand} {w.tractor_model} {w.plate && `· ${w.plate}`}</div>
                </div>
                <Badge className={STATUS[w.status]?.color}>{STATUS[w.status]?.label}</Badge>
              </div>
              {w.complaint && <div className="text-sm text-slate-700 line-clamp-2">{w.complaint}</div>}
              <div className="flex items-center justify-between pt-2 border-t border-slate-100">
                <div className="text-xs text-slate-500">{fmtDate(w.created_at)}</div>
                <div className="text-lg font-extrabold text-[#1B4D3E]">{fmtTRY(w.total)}</div>
              </div>
              <div className="flex gap-1">
                {w.status !== "teslim" && (
                  <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white flex-1 gap-1" onClick={() => deliver(w.id)} data-testid={`deliver-wo-${w.id}`}>
                    <CheckCircle2 size={14} /> Teslim Et
                  </Button>
                )}
                {w.status !== "teslim" && (
                  <Button size="sm" variant="outline" className="gap-1 border-[#D97706] text-[#D97706] hover:bg-[#D97706]/10" onClick={() => openPr(w)} data-testid={`request-part-${w.id}`}>
                    <PackagePlus size={14} /> Parça Talep
                  </Button>
                )}
                {w.status === "teslim" && (
                  <Button size="sm" variant="outline" className="gap-1" onClick={() => downloadFile(`/invoices/work-order/${w.id}.xml`, `fatura-${w.wo_no}.xml`)} data-testid={`invoice-wo-${w.id}`}>
                    <FileCode size={14} /> Fatura XML
                  </Button>
                )}
                <Button size="sm" variant="outline" onClick={() => edit(w)} data-testid={`edit-wo-${w.id}`}><Pencil size={14} /></Button>
                <Button size="sm" variant="outline" onClick={() => remove(w.id)} data-testid={`delete-wo-${w.id}`}><Trash2 size={14} className="text-rose-600" /></Button>
              </div>
            </CardContent>
          </Card>
        ))}
        {filtered.length === 0 && <div className="col-span-full text-center py-10 text-slate-500">İş emri yok. Yeni bir tane ekleyin.</div>}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editing ? "İş Emri Düzenle" : "Yeni İş Emri"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <CustomerPicker
                value={form.customer_id || ""}
                onChange={(c) => setForm((f) => ({ ...f, customer_id: c.id, customer_name: c.name }))}
                testid="wo-customer-picker"
                allowManualName
                manualName={form.customer_id ? "" : form.customer_name}
                onManualName={(v) => setForm((f) => ({ ...f, customer_name: v, customer_id: "" }))}
              />
            </div>
            <div><Label>Plaka</Label><Input value={form.plate} onChange={(e) => setForm({ ...form, plate: e.target.value })} data-testid="wo-plate" /></div>
            <div><Label>Marka</Label><Input value={form.tractor_brand} onChange={(e) => setForm({ ...form, tractor_brand: e.target.value })} data-testid="wo-brand" /></div>
            <div><Label>Model</Label><Input value={form.tractor_model} onChange={(e) => setForm({ ...form, tractor_model: e.target.value })} data-testid="wo-model" /></div>
            <div><Label>Şasi No</Label><Input value={form.chassis_no} onChange={(e) => setForm({ ...form, chassis_no: e.target.value })} data-testid="wo-chassis" /></div>
            <div><Label>Çalışma Saati</Label><Input type="number" value={form.working_hours} onChange={(e) => setForm({ ...form, working_hours: e.target.value })} data-testid="wo-hours" /></div>
            <div><Label>Teknisyen</Label><Input value={form.technician} onChange={(e) => setForm({ ...form, technician: e.target.value })} data-testid="wo-tech" /></div>
            <div><Label>Durum</Label>
              <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                <SelectTrigger data-testid="wo-status"><SelectValue /></SelectTrigger>
                <SelectContent>{Object.entries(STATUS).map(([k, v]) => <SelectItem key={k} value={k}>{v.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="sm:col-span-2"><Label>Şikayet</Label><Textarea value={form.complaint} onChange={(e) => setForm({ ...form, complaint: e.target.value })} data-testid="wo-complaint" /></div>
            <div className="sm:col-span-2"><Label>Teşhis / Yapılan İşlem</Label><Textarea value={form.diagnosis} onChange={(e) => setForm({ ...form, diagnosis: e.target.value })} data-testid="wo-diag" /></div>
          </div>

          <div className="mt-4">
            <Label>Kullanılan Yedek Parçalar</Label>
            <PartSearchPicker parts={parts} onPick={addPartLine} />
            <div className="mt-2 space-y-2">
              {form.parts.map((p, i) => (
                <div key={i} className="grid grid-cols-12 gap-2 items-center bg-slate-50 border border-slate-200 rounded-lg p-2">
                  <div className="col-span-5 text-sm font-medium truncate">{p.name}</div>
                  <Input type="number" className="col-span-2" value={p.quantity} onChange={(e) => updatePartLine(i, { quantity: e.target.value })} data-testid={`wo-part-qty-${i}`} />
                  <Input type="number" className="col-span-3" value={p.unit_price} onChange={(e) => updatePartLine(i, { unit_price: e.target.value })} />
                  <div className="col-span-1 text-right text-xs font-bold">{fmtTRY(Number(p.quantity) * Number(p.unit_price))}</div>
                  <button type="button" className="col-span-1 text-rose-600" onClick={() => removePartLine(i)}><X size={16} /></button>
                </div>
              ))}
            </div>
          </div>

          <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-3 items-end">
            <div><Label>İşçilik Saati</Label><Input type="number" step="0.25" value={form.labor_hours} onChange={(e) => setForm({ ...form, labor_hours: e.target.value })} data-testid="wo-labor-hours" /></div>
            <div><Label>Saat Ücreti (₺)</Label><Input type="number" value={form.labor_rate} onChange={(e) => setForm({ ...form, labor_rate: e.target.value })} data-testid="wo-labor-rate" /></div>
            <div>
              <Label>İşçilik Bedeli</Label>
              <div className="h-10 px-3 grid items-center border border-slate-300 rounded-md bg-slate-50 text-sm font-bold text-slate-900" data-testid="wo-labor-cost">
                {fmtTRY(Number(form.labor_hours || 0) * Number(form.labor_rate || 0) || Number(form.labor_cost || 0))}
              </div>
            </div>
            <div className="text-right">
              <div className="text-xs uppercase tracking-widest text-slate-500 font-bold">Toplam</div>
              <div className="text-2xl font-extrabold text-[#1B4D3E]" data-testid="wo-total">{fmtTRY(total)}</div>
            </div>
          </div>

          <div className="mt-3">
            <Label>Notlar</Label>
            <Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="wo-notes" />
          </div>
          <div className="mt-3">
            <Label>Fotoğraf / Video (öncesi/sonrası)</Label>
            <MediaUploader value={form.media} onChange={(m) => setForm({ ...form, media: m })} testid="wo-media" />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>İptal</Button>
            <Button className="bg-[#1B4D3E] hover:bg-[#163e32]" onClick={submit} data-testid="save-wo-btn">Kaydet</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Parça Talep Modal */}
      <Dialog open={!!prFor} onOpenChange={(v) => !v && setPrFor(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>Parça Talebi Oluştur</DialogTitle></DialogHeader>
          {prFor && (
            <div className="space-y-3">
              <div className="text-xs text-slate-500 bg-slate-50 border rounded p-2">
                İş Emri: <span className="font-mono font-bold">{prFor.wo_no}</span> · {prFor.customer_name} · {prFor.tractor_brand} {prFor.tractor_model}
              </div>
              <div>
                <Label>Parça (stoktan)</Label>
                <Select value={prForm.part_id || ""} onValueChange={(v) => {
                  const p = parts.find((x) => x.id === v);
                  setPrForm({ ...prForm, part_id: v, part_name: p?.name || "" });
                }}>
                  <SelectTrigger data-testid="wo-pr-part"><SelectValue placeholder="Stoktan seç" /></SelectTrigger>
                  <SelectContent>{parts.map((p) => <SelectItem key={p.id} value={p.id}>{p.name} ({p.quantity} adet)</SelectItem>)}</SelectContent>
                </Select>
                <div className="text-xs text-slate-500 mt-1">veya elle parça adı:</div>
                <Input className="mt-1" value={prForm.part_name} onChange={(e) => setPrForm({ ...prForm, part_name: e.target.value })} placeholder="Örn: Debriyaj balatası" data-testid="wo-pr-name" />
              </div>
              <div><Label>Adet</Label><Input type="number" value={prForm.quantity} onChange={(e) => setPrForm({ ...prForm, quantity: e.target.value })} data-testid="wo-pr-qty" /></div>
              <div><Label>Aciliyet / Açıklama</Label><Textarea value={prForm.note} onChange={(e) => setPrForm({ ...prForm, note: e.target.value })} placeholder="Örn: Acil, müşteri bekliyor" data-testid="wo-pr-note" /></div>
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded p-2">
                Talebi gönderdiğinizde depo personeline anlık bildirim düşecek.
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setPrFor(null)}>İptal</Button>
            <Button className="bg-[#D97706] hover:bg-[#B45309]" onClick={submitPr} data-testid="wo-pr-submit"><PackagePlus size={14} className="mr-1" /> Talep Gönder</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
